// Web-exclusive Candidates screen - Applicant Review for Facilities
// Phase 4: Review and manage job applicants

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import WebLayout from '../../components/web/WebLayout';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import { getJobApplications, getJob, approveJob, rejectJob } from '../../services/job';
import { useIsMobile } from '../../hooks/useIsMobile';
import MobileDetailBackBar from '../../components/web/MobileDetailBackBar';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const CandidatesScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const queryClient = useQueryClient();
  const jobId = route.params?.jobId;

  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState('list'); // mobile only: 'list' | 'detail'

  // Fetch job details
  const { data: jobData } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJob(jobId),
    enabled: !!jobId,
  });

  const job = jobData?.data?.data || jobData?.data || {};

  // Fetch job applications
  const { data: applicationsData, isLoading } = useQuery({
    queryKey: ['jobApplications', jobId],
    queryFn: () => getJobApplications(jobId),
    enabled: !!jobId,
  });

  const applications = applicationsData?.data?.data || applicationsData?.data || [];

  // Filter applications
  const filteredApplications = applications.filter(app => {
    const name = app.user?.name || app.doctor?.name || '';
    const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase());
    const status = (app.status || 'pending').toLowerCase();
    const matchesFilter = activeFilter === 'all' || status === activeFilter;
    return matchesSearch && matchesFilter;
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (uuid) => approveJob({ uuid }),
    onSuccess: () => {
      queryClient.invalidateQueries(['jobApplications', jobId]);
      setSelectedCandidate(null);
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: (uuid) => rejectJob({ uuid }),
    onSuccess: () => {
      queryClient.invalidateQueries(['jobApplications', jobId]);
      setSelectedCandidate(null);
    },
  });

  const handleApprove = (applicationId) => {
    approveMutation.mutate(applicationId);
  };

  const handleReject = (applicationId) => {
    if (confirm('Are you sure you want to reject this application?')) {
      rejectMutation.mutate(applicationId);
    }
  };

  // Get application counts by status
  const getStatusCount = (status) => {
    if (status === 'all') return applications.length;
    return applications.filter(app =>
      (app.status || 'pending').toLowerCase() === status
    ).length;
  };

  return (
    <WebLayout>
      {/* Header */}
      <View style={[styles.headerRow, isMobile && styles.headerRowMobile]}>
        <View style={styles.headerLeft}>
          <TouchableOpacity
            style={styles.backBtn}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backBtnText}>{'\u2190'} Back</Text>
          </TouchableOpacity>
          <View>
            <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Candidates</Text>
            <Text style={styles.pageSubtitle}>
              {job.title || job.profession?.name || 'Job'} {'\u2022'} {applications.length} applicants
            </Text>
          </View>
        </View>
        <TouchableOpacity
          style={styles.btnViewJob}
          onPress={() => navigation.navigate('JobDescription', { jobId })}
        >
          <Text style={styles.btnViewJobText}>View Job</Text>
        </TouchableOpacity>
      </View>

      {/* Search and Filters */}
      <View style={styles.filterSection}>
        <View style={styles.searchWrapper}>
          <Text style={styles.searchIcon}>{'\u{1F50D}'}</Text>
          <TextInput
            style={[styles.searchInput, isMobile && styles.searchInputMobile]}
            placeholder="Search candidates..."
            placeholderTextColor={webColors.textMuted}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        <View style={[styles.statusFilters, isMobile && styles.statusFiltersMobile]}>
          {STATUS_FILTERS.map((filter) => (
            <TouchableOpacity
              key={filter.id}
              style={[
                styles.filterBtn,
                activeFilter === filter.id && styles.filterBtnActive,
              ]}
              onPress={() => setActiveFilter(filter.id)}
            >
              <Text style={[
                styles.filterBtnText,
                activeFilter === filter.id && styles.filterBtnTextActive,
              ]}>
                {filter.label}
              </Text>
              <View style={[
                styles.filterCount,
                activeFilter === filter.id && styles.filterCountActive,
              ]}>
                <Text style={[
                  styles.filterCountText,
                  activeFilter === filter.id && styles.filterCountTextActive,
                ]}>
                  {getStatusCount(filter.id)}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Content Layout */}
      <View style={[styles.contentLayout, isMobile && styles.contentLayoutMobile]}>
        {/* Candidates List */}
        {(!isMobile || mobileView === 'list') && (
        <View style={styles.candidatesListContainer}>
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={webColors.primary} />
              <Text style={styles.loadingText}>Loading candidates...</Text>
            </View>
          ) : filteredApplications.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyIcon}>{'\u{1F465}'}</Text>
              <Text style={styles.emptyTitle}>No candidates found</Text>
              <Text style={styles.emptyText}>
                {searchQuery
                  ? 'Try a different search term'
                  : activeFilter !== 'all'
                    ? `No ${activeFilter} applications`
                    : 'No applications received yet'}
              </Text>
            </View>
          ) : (
            <View style={styles.candidatesList}>
              {filteredApplications.map((app) => {
                const isSelected = selectedCandidate?.id === (app.id || app.uuid);
                return (
                  <TouchableOpacity
                    key={app.id || app.uuid}
                    style={[
                      styles.candidateCard,
                      isSelected && styles.candidateCardSelected,
                    ]}
                    onPress={() => {
                      setSelectedCandidate(app);
                      if (isMobile) setMobileView('detail');
                    }}
                  >
                    <View style={styles.candidateAvatar}>
                      <Text style={styles.candidateInitials}>
                        {getInitials(app.user?.name || app.doctor?.name || 'U')}
                      </Text>
                    </View>
                    <View style={styles.candidateInfo}>
                      <Text style={styles.candidateName}>
                        {app.user?.name || app.doctor?.name || 'Unknown'}
                      </Text>
                      <Text style={styles.candidateProfession}>
                        {app.user?.profession || app.doctor?.profession?.name || 'Healthcare Professional'}
                      </Text>
                      <Text style={styles.candidateDate}>
                        Applied {formatDate(app.created_at)}
                      </Text>
                    </View>
                    <View style={[styles.candidateStatus, getStatusStyle(app.status)]}>
                      <Text style={[styles.candidateStatusText, getStatusTextStyle(app.status)]}>
                        {app.status || 'Pending'}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>
        )}

        {/* Candidate Details Panel */}
        {(!isMobile || mobileView === 'detail') && (
        <View style={[styles.detailsPanel, isMobile && styles.detailsPanelMobile]}>
          {isMobile && (
            <MobileDetailBackBar label="Back to candidates" onPress={() => setMobileView('list')} />
          )}
          {selectedCandidate ? (
            <View style={styles.detailsContent}>
              {/* Candidate Header */}
              <View style={styles.detailsHeader}>
                <View style={styles.detailsAvatar}>
                  <Text style={styles.detailsAvatarText}>
                    {getInitials(selectedCandidate.user?.name || selectedCandidate.doctor?.name || 'U')}
                  </Text>
                </View>
                <Text style={styles.detailsName}>
                  {selectedCandidate.user?.name || selectedCandidate.doctor?.name || 'Unknown'}
                </Text>
                <Text style={styles.detailsProfession}>
                  {selectedCandidate.user?.profession || selectedCandidate.doctor?.profession?.name || 'Healthcare Professional'}
                </Text>
                <View style={[styles.detailsStatusBadge, getStatusStyle(selectedCandidate.status)]}>
                  <Text style={[styles.detailsStatusText, getStatusTextStyle(selectedCandidate.status)]}>
                    {selectedCandidate.status || 'Pending'}
                  </Text>
                </View>
              </View>

              {/* Contact Info */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Contact Information</Text>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Email</Text>
                  <Text style={styles.detailsValue}>
                    {selectedCandidate.user?.email || selectedCandidate.doctor?.email || '-'}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Phone</Text>
                  <Text style={styles.detailsValue}>
                    {selectedCandidate.user?.phone || selectedCandidate.doctor?.phone || '-'}
                  </Text>
                </View>
              </View>

              {/* Experience */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Experience</Text>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Years of Experience</Text>
                  <Text style={styles.detailsValue}>
                    {selectedCandidate.user?.experience_years || selectedCandidate.doctor?.experience_years || '-'}
                  </Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Specialization</Text>
                  <Text style={styles.detailsValue}>
                    {selectedCandidate.user?.specialization || selectedCandidate.doctor?.specialization?.name || '-'}
                  </Text>
                </View>
              </View>

              {/* Application Details */}
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>Application</Text>
                <View style={styles.detailsRow}>
                  <Text style={styles.detailsLabel}>Applied On</Text>
                  <Text style={styles.detailsValue}>
                    {formatFullDate(selectedCandidate.created_at)}
                  </Text>
                </View>
                {selectedCandidate.cover_letter && (
                  <View style={styles.coverLetterBox}>
                    <Text style={styles.coverLetterLabel}>Cover Letter</Text>
                    <Text style={styles.coverLetterText}>
                      {selectedCandidate.cover_letter}
                    </Text>
                  </View>
                )}
              </View>

              {/* Actions */}
              {(selectedCandidate.status || 'pending').toLowerCase() === 'pending' && (
                <View style={[styles.detailsActions, isMobile && styles.detailsActionsMobile]}>
                  <TouchableOpacity
                    style={styles.btnReject}
                    onPress={() => handleReject(selectedCandidate.uuid)}
                    disabled={rejectMutation.isLoading}
                  >
                    <Text style={styles.btnRejectText}>
                      {rejectMutation.isLoading ? 'Rejecting...' : 'Reject'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.btnApprove}
                    onPress={() => handleApprove(selectedCandidate.uuid)}
                    disabled={approveMutation.isLoading}
                  >
                    <Text style={styles.btnApproveText}>
                      {approveMutation.isLoading ? 'Approving...' : 'Approve'}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          ) : (
            <View style={styles.detailsEmpty}>
              <Text style={styles.detailsEmptyIcon}>{'\u{1F464}'}</Text>
              <Text style={styles.detailsEmptyText}>Select a candidate to view details</Text>
            </View>
          )}
        </View>
        )}
      </View>
    </WebLayout>
  );
};

// Helper functions
const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getStatusStyle = (status) => {
  const styles = {
    pending: { backgroundColor: '#fef3c7' },
    approved: { backgroundColor: '#dcfce7' },
    accepted: { backgroundColor: '#dcfce7' },
    rejected: { backgroundColor: '#fee2e2' },
    declined: { backgroundColor: '#fee2e2' },
  };
  return styles[status?.toLowerCase()] || styles.pending;
};

const getStatusTextStyle = (status) => {
  const styles = {
    pending: { color: '#92400e' },
    approved: { color: '#166534' },
    accepted: { color: '#166534' },
    rejected: { color: '#b91c1c' },
    declined: { color: '#b91c1c' },
  };
  return styles[status?.toLowerCase()] || styles.pending;
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  const now = new Date();
  const diff = now - d;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

const formatFullDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 32,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  backBtn: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
  },
  backBtnText: {
    fontSize: 14,
    color: webColors.textSecondary,
    fontWeight: '500',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textMuted,
  },
  btnViewJob: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
  },
  btnViewJobText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  filterSection: {
    marginBottom: 24,
  },
  searchWrapper: {
    position: 'relative',
    marginBottom: 16,
  },
  searchIcon: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: [{ translateY: -9 }],
    fontSize: 16,
    zIndex: 1,
  },
  searchInput: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingHorizontal: 48,
    paddingVertical: 14,
    fontSize: 15,
    color: webColors.textPrimary,
    maxWidth: 400,
  },
  statusFilters: {
    flexDirection: 'row',
    gap: 12,
  },
  filterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
  },
  filterBtnActive: {
    backgroundColor: webColors.primaryLight,
    borderColor: webColors.primary,
  },
  filterBtnText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  filterBtnTextActive: {
    color: webColors.primary,
    fontWeight: '600',
  },
  filterCount: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 10,
  },
  filterCountActive: {
    backgroundColor: webColors.primary,
  },
  filterCountText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textMuted,
  },
  filterCountTextActive: {
    color: '#ffffff',
  },
  contentLayout: {
    flexDirection: 'row',
    gap: 24,
  },
  candidatesListContainer: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    overflow: 'hidden',
  },
  loadingContainer: {
    alignItems: 'center',
    padding: 48,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: webColors.textSecondary,
  },
  emptyState: {
    alignItems: 'center',
    padding: 48,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
  },
  candidatesList: {
    padding: 12,
    gap: 8,
  },
  candidateCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: 'transparent',
    borderRadius: webBorderRadius.medium,
  },
  candidateCardSelected: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  candidateAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  candidateInitials: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.primary,
  },
  candidateInfo: {
    flex: 1,
  },
  candidateName: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  candidateProfession: {
    fontSize: 13,
    color: webColors.textSecondary,
    marginBottom: 2,
  },
  candidateDate: {
    fontSize: 12,
    color: webColors.textMuted,
  },
  candidateStatus: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: webBorderRadius.small,
  },
  candidateStatusText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  detailsPanel: {
    width: 400,
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    overflow: 'hidden',
  },
  detailsContent: {
    padding: 24,
  },
  detailsHeader: {
    alignItems: 'center',
    paddingBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
    marginBottom: 24,
  },
  detailsAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  detailsAvatarText: {
    fontSize: 28,
    fontWeight: '600',
    color: webColors.primary,
  },
  detailsName: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  detailsProfession: {
    fontSize: 14,
    color: webColors.textSecondary,
    marginBottom: 12,
  },
  detailsStatusBadge: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: webBorderRadius.pill,
  },
  detailsStatusText: {
    fontSize: 13,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  detailsSection: {
    marginBottom: 24,
  },
  detailsSectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  detailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  detailsLabel: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  detailsValue: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textPrimary,
  },
  coverLetterBox: {
    marginTop: 12,
    padding: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
  },
  coverLetterLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textMuted,
    marginBottom: 8,
  },
  coverLetterText: {
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 20,
  },
  detailsActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  btnReject: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnRejectText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  btnApprove: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnApproveText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  detailsEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
    minHeight: 400,
  },
  detailsEmptyIcon: {
    fontSize: 48,
    marginBottom: 16,
    opacity: 0.5,
  },
  detailsEmptyText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
  },
  headerRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
    marginBottom: 20,
  },
  pageTitleMobile: {
    fontSize: 22,
  },
  searchInputMobile: {
    maxWidth: '100%',
  },
  statusFiltersMobile: {
    flexWrap: 'wrap',
    gap: 8,
  },
  contentLayoutMobile: {
    flexDirection: 'column',
    gap: 0,
  },
  detailsPanelMobile: {
    width: '100%',
  },
  detailsActionsMobile: {
    flexDirection: 'column',
  },
});

export default CandidatesScreen;
